import React, { ChangeEvent, useState } from "react";
import {
  Grid,
  Typography,
  TextField,
  FormHelperText,
  Button,
} from "@mui/material";
import { IAlert } from "../../interfaces/alert";
import { IValidation } from "../../interfaces/form";
import { IMerchantOrClientInvitationModal } from "../../interfaces/modal";
import { SUCCESS, ERROR, ERRORS_MESSAGES } from "../../utils/constants";
import { validateInputs } from "../../utils/inputValidations";
import GenericAlert from "../GenericAlert";
import ModalBase from "./ModalBase";
import GenericForm from "../GenericForm";
import { sendMerchantInvitations } from "../../requests/merchantInvitationRequests";

const { emailInvalid, emailRequired } = ERRORS_MESSAGES;

const formValidations: IValidation[] = [
  {
    id: "email",
    type: "required",
    message: emailRequired,
  },
  {
    id: "email",
    type: "email",
    message: emailInvalid,
  },
];

/**
 * Modal for sending merchant invitations from a marketplace
 *
 * Note: For internal users registering clients, use AddClientModal instead
 */
const SendInvitationModal = ({
  open,
  setOpen,
  setUpdateTable,
  updateTable,
  setAlert,
}: IMerchantOrClientInvitationModal) => {
  const [invitationInfo, setInvitationInfo] = useState({ email: "" });
  const [errors, setErrors] = useState({ email: [] as string[] });
  const [alertModal, setAlertModal] = useState({} as IAlert);
  const [disableButton, setDisableButton] = useState(false);

  const handleClose = () => {
    setOpen(false);
    setErrors({ email: [] });
    setInvitationInfo({ email: "" });
    setDisableButton(false);
    setAlertModal({} as IAlert);
  };

  const handleSubmit = async () => {
    const inputErrors = validateInputs(invitationInfo, formValidations);
    setErrors(inputErrors as { email: string[] });

    if (Object.keys(inputErrors).length === 0) {
      setDisableButton(true);
      try {
        const { message } = await sendMerchantInvitations([
          invitationInfo.email,
        ]);
        setAlert({
          typeOfAlert: SUCCESS,
          message,
          showAlert: true,
        });
        setUpdateTable(!updateTable);
        handleClose();
      } catch (error: any) {
        setAlertModal({
          typeOfAlert: ERROR,
          message: error,
          showAlert: true,
        });
        setDisableButton(false);
      }
    }
  };

  return (
    <ModalBase open={open} handleClose={handleClose}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography className="m-t-10 f-s-20">Añadir Merchant</Typography>
          {alertModal.showAlert && <GenericAlert alert={alertModal} />}
        </Grid>

        <Grid item xs={12} className="m-t-20">
          <GenericForm onSubmit={handleSubmit} disableSubmit={disableButton}>
            <Typography className="f-s-14 m-b-10">
              Correo electrónico
            </Typography>
            <TextField
              fullWidth
              size="small"
              name="billingEmail"
              placeholder="Ingresar correo electrónico"
              value={invitationInfo.email}
              error={errors.email?.length > 0}
              onChange={({
                target: { value },
              }: ChangeEvent<HTMLInputElement>) => {
                setAlertModal({ showAlert: false } as IAlert);
                setDisableButton(false);
                setInvitationInfo({ email: value.trim() });
              }}
              autoFocus
            />
            {errors.email?.length > 0 &&
              errors.email.map((message: string) => (
                <FormHelperText className="color-error" key={message}>
                  {message}
                </FormHelperText>
              ))}
          </GenericForm>
        </Grid>

        <Grid item xs={6} className="m-t-40">
          <Button fullWidth variant="outlined" onClick={handleClose}>
            Cancelar
          </Button>
        </Grid>
        <Grid item xs={6} className="m-t-40">
          <Button
            fullWidth
            variant="contained"
            disabled={disableButton}
            onClick={handleSubmit}
          >
            Enviar invitación
          </Button>
        </Grid>
      </Grid>
    </ModalBase>
  );
};

export default SendInvitationModal;
